<template>
  <v-card flat>
    <v-col cols="12" class="pa-0">
      <v-row no-gutters>
        <v-btn color="primary" icon @click="handleGetBack(false)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="text-h6 mr-2" style="vertical-align: middle">
          {{ $t('message.pages.video.miniTrigger.miniTrigger') }}
          <learn-more-btn :onClickBtn="handleGoToArticle" />
        </span>
        <v-spacer />
        <v-btn
          v-if="!isEditing && hasEditVideoPerm"
          class="mr-4"
          color="primary"
          @click="handleEditCta(false)"
        >
          {{ $t('message.add') }}</v-btn
        >
      </v-row>
    </v-col>
    <div v-if="isEditing" class="px-2 py-0 my-2">
      <span> {{ $t('message.pages.video.miniTrigger.description') }}</span>
    </div>

    <v-col cols="12" class="mt-2 ml-0" v-if="!isEditing">
      <v-card
        class="overflow-y-auto"
        flat
        max-height="240px"
        width="100%"
        v-if="allCtasVideo.length"
      >
        <v-row no-gutters>
          <v-col cols="12" v-for="(cta, i) in allCtasVideo" :key="i">
            <v-card class="mt-0 pt-0 pl-2 mr-1 mb-2" outlined height="45px">
              <v-row class="fill-height" no-gutters align="center">
                <span>{{ cta.title }}</span>
                <v-spacer />
                <v-progress-circular
                  v-if="loadings.removingCta && beingRemovedCtaId === cta.id"
                  class="mr-2"
                  indeterminate
                  color="primary"
                  size="22"
                  width="3"
                ></v-progress-circular>
                <v-menu bottom left offset-y v-if="hasEditVideoPerm">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn class="mr-2" icon v-bind="attrs" v-on="on">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense class="pa-0" width="120px">
                    <v-list-item @click="handleEditCta(cta, i)">
                      <v-list-item-title>{{
                        $t('message.pages.videoPlayerButtons.edit')
                      }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="handleRemoveCta(cta)">
                      <v-list-item-title class="error--text">{{
                        $t('message.pages.videoEditModal.delete')
                      }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-card>
    </v-col>
    <v-col cols="12" v-else class="px-2">
      <v-row no-gutters>
        <v-col cols="12">
          <input-images
            @handleInput="(data) => saveImageLink(data)"
            :previewImage="editingMiniTrigger.metadata.previewImage.url"
          />
        </v-col>
        <v-col cols="12" class="mt-6">
          <v-row no-gutters>
            <v-col cols="12" class="pr-2">
              {{ $t('message.pages.video.miniTrigger.name') }}</v-col
            >
          </v-row>
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="editingMiniTrigger.title"
            dense
            outlined
            hide-details
            :placeholder="$t('message.pages.video.miniTrigger.name')"
            :disabled="!hasEditVideoPerm"
          ></v-text-field>
        </v-col>
        <v-col cols="12" class="mt-2">
          <v-row no-gutters>
            <v-col cols="6" class="pr-2">{{
              $t('message.pages.videoPlayerButtons.start')
            }}</v-col>
            <v-col cols="6" class="pl-2">{{
              $t('message.pages.videoPlayerButtons.end')
            }}</v-col>
          </v-row>
        </v-col>
        <v-col cols="12">
          <v-row no-gutters>
            <v-col cols="6" class="pr-2">
              <!-- start -->
              <AntTimePicker
                :startVal="editingMiniTrigger.metadata.startTime"
                :timeVideo="timeVideo"
                :disabled="
                  editingMiniTrigger.metadata.lastAllVideo || !hasEditVideoPerm
                "
                @changedTime="
                  (time) => (editingMiniTrigger.metadata.startTime = time)
                "
              />
            </v-col>
            <v-col cols="6" class="pl-2">
              <!-- end -->
              <AntTimePicker
                :key="
                  isLowerOrEqual
                    ? editingMiniTrigger.metadata.startTime
                    : editingMiniTrigger.metadata.endTime
                "
                :startVal="
                  isLowerOrEqual
                    ? editingMiniTrigger.metadata.startTime
                    : editingMiniTrigger.metadata.endTime
                "
                :thresholdTime="editingMiniTrigger.metadata.startTime"
                :timeVideo="timeVideo"
                :disabled="
                  editingMiniTrigger.metadata.lastAllVideo ||
                  editingMiniTrigger.metadata.lastTillEnd ||
                  !hasEditVideoPerm
                "
                @changedTime="
                  (time) => (editingMiniTrigger.metadata.endTime = time)
                "
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" class="mt-2">
          <v-row no-gutters>
            <v-col cols="6" class="pr-2">
              <v-checkbox
                v-model="editingMiniTrigger.metadata.lastAllVideo"
                :label="$t('message.pages.videoPlayerButtons.lastAllVideo')"
                class="pa-0 ma-0"
                color="primary"
                hide-details
                :disabled="!hasEditVideoPerm"
                @click="handleLastAllVideo"
              ></v-checkbox>
            </v-col>
            <v-col cols="6" class="pl-2">
              <v-checkbox
                v-model="editingMiniTrigger.metadata.lastTillEnd"
                :label="$t('message.pages.videoPlayerButtons.lastTillEnd')"
                dense
                class="pa-0 ma-0"
                color="primary"
                hide-details
                :disabled="!hasEditVideoPerm"
                @click="handleLastTillEnd"
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="12"
          class="d-flex flex-row align-end mt-8"
          v-if="hasEditVideoPerm"
        >
          <v-spacer />
          <v-btn min-width="150" plain @click="handleCancelEditing">{{
            $t('message.pages.videoPlayerButtons.cancel')
          }}</v-btn>
          <v-btn
            min-width="150"
            color="primary"
            :disabled="loadings.creatingCta"
            :loading="loadings.creatingCta"
            @click="handlePublishNewCta"
            >{{ $t('message.pages.videoPlayerButtons.publish') }}</v-btn
          >
        </v-col>
      </v-row>
    </v-col>
    <unsaved-changes-modal />
  </v-card>
</template>

<script>
import InputImages from '@/components/InputImages.vue'
import { mapMutations, mapState } from 'vuex'
import _ from 'lodash'
import playerApi from '@/services/playerApi'
import { v4 as uuidv4 } from 'uuid'
import i18n from '@/translations/lang.js'
import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue'
import imagesApi from '@/services/imagesApi.js'
import LearnMoreBtn from '@/components/LearnMoreBtn.vue'
import AntTimePicker from '@/components/AntTimePicker.vue'

export default {
  name: 'VideoPlayerMiniTrigger',
  components: {
    InputImages,
    UnsavedChangesModal,
    LearnMoreBtn,
    AntTimePicker,
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    customVideoId: {
      required: false,
    },
  },
  data: () => ({
    isEditing: false,
    timeVideo: 0,
    loadings: {
      creatingCta: false,
      removingCta: false,
    },
    editingMiniTrigger: {},
    initialEditingMiniTrigger: {},
    defaultEditingMiniTrigger: {
      id: null,
      title: i18n.t('message.pages.video.miniTrigger.namePlaceHolder'),
      metadata: {
        lastAllVideo: false,
        lastTillEnd: false,
        previewImage: {
          url: '',
          id: null,
        },
        endTime: '00:05',
        startTime: '00:00',
      },
    },
    incomingCta: false,
    imageName: '',
    articleLinks: {
      pt: 'https://help.pandavideo.com/pt-br/article/como-funciona-e-como-usar-os-mini-gatilhos-zvgkcr/',
      en: 'https://help.pandavideo.com/en-us/article/how-does-it-work-and-how-to-use-the-mini-triggers-9mz2ji/',
    },
  }),
  watch: {
    editingMiniTrigger: {
      handler() {
        if (this.isEditing) {
          this.checkPreview()
        }
      },
      deep: true,
    },
  },
  computed: {
    ...mapState({
      video: (state) => state.video.video,
    }),
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    noChanges() {
      return _.isEqual(this.editingMiniTrigger, this.initialEditingMiniTrigger)
    },
    allCtasVideo: {
      get() {
        const videoCtas = this.video?.config?.ctas ?? []
        if (videoCtas.length) {
          return videoCtas.filter((cta) => cta.metadata?.isMiniTrigger)
        }
        return []
      },
      cache: false,
    },
    isLowerOrEqual() {
      const { endTime, startTime } = this.editingMiniTrigger.metadata
      return endTime < startTime
    },
  },
  methods: {
    ...mapMutations('video', ['UPDATE_VIDEO']),
    handleGoToArticle() {
      const articleLink =
        this.articleLinks[this.$i18next.resolvedLanguage] ??
        this.articleLinks['en']
      window.open(articleLink, '_blank')
    },
    async handleRemoveCta(cta, dontRefreshConfig, oldVideoID) {
      const videoID = oldVideoID ?? this.video.id

      try {
        this.loadings.removingCta = true
        this.beingRemovedCtaId = cta.id

        const { data } = await playerApi.deleteCta(videoID, cta.id)
        if (
          cta?.metadata?.previewImage?.url &&
          cta?.metadata?.previewImage?.id
        ) {
          await imagesApi.deleteImage(cta.metadata.previewImage.id)
        }

        this.video.config = data
        this.UPDATE_VIDEO(this.video)
        if (!dontRefreshConfig) this.refreshConfig()

        this.loadings.removingCta = false
        this.error = ''

        this.isEditing = this.allCtasVideo.length === 0
      } catch (error) {
        this.error = this.$t(
          'message.pages.video.buttonsPage.errors.removeError'
        )
        this.loadings.removingCta = false
        console.error(error)
      }
    },
    handleEditCta(cta, i) {
      this.resetFormData()
      if (cta) {
        this.editingMiniTrigger = _.cloneDeep(cta)
        this.initialEditingMiniTrigger = _.cloneDeep(cta)
        this.incomingCta = i
      } else {
        this.incomingCta = false
      }
      this.isEditing = true
    },
    saveImageLink(data) {
      this.imageName = data.file?.name ? data.file?.name : ''
      this.editingMiniTrigger.metadata.previewImage.url = data.filePreview
        ? data.filePreview
        : ''
      this.checkPreview()
    },
    async checkPreview() {
      const cta = await this.generateCta()

      if (this.video.hasOwnProperty('video_source')) {
        const ctas = cta?.metadata?.previewImage?.url
          ? [cta]
          : this.video.config.ctas
        this.$root.$emit('setCtas', ctas)
      } else {
        const iframe = document.getElementById('panda-player').contentWindow
        iframe.postMessage(
          {
            type: 'ctas',
            parameter: {
              ctas: cta?.metadata?.previewImage?.url
                ? [cta]
                : this.video.config.ctas,
            },
          },
          '*'
        )
      }
    },
    handleGetBack(ignoreEquality = false) {
      if (!this.noChanges && !ignoreEquality && this.isEditing) {
        this.$root.$emit('unsavedChangesModal', false, false, true)
      } else {
        if (this.isEditing && this.allCtasVideo.length !== 0) {
          this.isEditing = false
        } else {
          this.handleBack()
        }
      }
    },
    refreshConfig() {
      if (this.video.hasOwnProperty('video_source')) {
        this.$root.$emit('setCtas', this.video.config.ctas)
      } else {
        const iframe = document.getElementById('panda-player').contentWindow
        iframe.postMessage(
          {
            type: 'ctas',
            parameter: {
              ctas: this.video.config.ctas,
            },
          },
          '*'
        )
      }
    },
    formatTimeToSeconds(time) {
      let res = time.split(':').reduce((acc, time) => 60 * acc + +time)
      return res
    },
    async generateCta(isDeploy = false) {
      const id = uuidv4()

      if (!this.editingMiniTrigger.id || this.editingMiniTrigger.id === '') {
        this.editingMiniTrigger.id = id
        this.initialEditingMiniTrigger.id = id
      }

      if (isDeploy) this.editingMiniTrigger.id = id

      let time = this.formatTimeToSeconds(
        this.editingMiniTrigger.metadata.startTime
      )
      let duration =
        this.formatTimeToSeconds(this.editingMiniTrigger.metadata.endTime) -
        this.formatTimeToSeconds(this.editingMiniTrigger.metadata.startTime)

      if (duration < 1) {
        duration = 0
      }
      if (this.editingMiniTrigger.metadata.lastAllVideo) {
        time = 0
        duration = this.timeVideo
      }
      if (this.editingMiniTrigger.metadata.lastTillEnd) {
        duration = this.timeVideo
      }
      if (isDeploy) {
        const data = await fetch(
          this.editingMiniTrigger.metadata.previewImage.url
        )
        const blobData = await data.blob()
        const imageData = await imagesApi.handleUploadImage(blobData, [
          {
            filename:
              this.imageName ||
              this.initialEditingMiniTrigger.metadata.previewImage.url
                .split('/')
                .pop(),
            type: 'cta',
          },
        ])
        this.editingMiniTrigger.metadata.previewImage.url = `${imageData.cdn_base}/${imageData.storage_path}`
        this.editingMiniTrigger.metadata.previewImage.id = imageData.id
      }
      const cta = {
        id: this.editingMiniTrigger.id,
        title: this.editingMiniTrigger.title,
        type: 'video',
        time: time,
        duration: duration,
        script:
          '<script>function play() { window.parent.postMessage({type:"togglePlay",}, "*")};</scr' +
          `ipt><style> .miniTrigger-image { display: flex; max-width: 100%; height: 100%; justify-content: center; align-items: center; margin: auto; } body { overflow: hidden; } .miniTrigger-image img { max-width: 80%; max-height: 80% } </style><div class="miniTrigger-image" onclick="play()"><img src="${this.editingMiniTrigger.metadata.previewImage.url}" /></div>"`,
        metadata: {
          ...this.editingMiniTrigger.metadata,
          isMiniTrigger: true,
          fadeConfigs: { fadeInMs: 200, fadeOutMs: 200 },
        },
      }
      return cta
    },
    async handlePublishNewCta() {
      if (this.noChanges) return this.handleGetBack(true)

      this.loadings.creatingCta = true

      try {
        const generatedCtas = await this.generateCta(true)
        const oldVideoID = this.video.id

        let { data } = await playerApi.addCta(this.video.id, generatedCtas)
        this.video.config = data
        this.UPDATE_VIDEO(this.video)

        if (this.initialEditingMiniTrigger.metadata?.previewImage?.url) {
          await this.handleRemoveCta(
            this.initialEditingMiniTrigger,
            true,
            oldVideoID
          )

          this.isEditing = false
        } else {
          this.handleGetBack(true)
          this.refreshConfig()
        }
      } catch (err) {
        console.error(err)
      } finally {
        this.loadings.creatingCta = false
      }
    },
    handleCancelEditing() {
      this.isEditing = false
      this.resetFormData()
      this.checkPreview()

      if (this.allCtasVideo.length === 0) this.handleGetBack(false)
    },
    resetFormData() {
      this.editingMiniTrigger = _.cloneDeep(this.defaultEditingMiniTrigger)
      this.initialEditingMiniTrigger = _.cloneDeep(this.editingMiniTrigger)
    },
    handleLastAllVideo() {
      if (this.editingMiniTrigger.lastAllVideo) {
        this.editingMiniTrigger.startTime = '00:00'
        this.editingMiniTrigger.endTime = this.formatSecondsToTime(
          this.timeVideo
        )
        this.editingMiniTrigger.lastTillEnd = false
      }
    },
    handleLastTillEnd() {
      if (this.editingMiniTrigger.lastTillEnd) {
        this.editingMiniTrigger.endTime = this.formatSecondsToTime(
          this.timeVideo
        )
        this.editingMiniTrigger.lastAllVideo = false
      }
    },
    formatSecondsToTime(seconds) {
      const res = new Date(seconds * 1000).toISOString()
      if (this.timeVideo < 3600) {
        return res.substring(14, 19)
      }
      return res.substring(11, 19)
    },
  },
  mounted() {
    this.$root.$on('discardChangesMiniTriggerPage', () => {
      this.editingMiniTrigger = { ...this.initialEditingMiniTrigger }
      this.handleGetBack()
    })
    this.resetFormData()
    this.timeVideo = this.$store.state.video.video?.length
    this.isEditing = this.allCtasVideo.length === 0
  },
}
</script>
